import WithAuth from "../../hoc/WithAuth";
import { Input } from "../../components/ui/input";
import { Icons } from "../../assets/images/icons";
import { AppButton } from "../../components/ui/AppButton";
import { ExportButton } from "../../components/ui/ExportButton";
import { DataTable } from "../../components/ui/data-table";
import { Warehouse } from "../../types/warehouse";
import { useMemo, useState } from "react";
import { useWarehouseList } from "../../api/admin";
import { useTablePrint } from "../../hooks/useTablePrint";
import { Button } from "../../components/ui/button";
import CreateWareHouseModal from "../../components/modals/CreateWarehouseModal";

const WareHousePage = () => {
  /** State */
  const [searchText, setSearchText] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  const [open, setOpen] = useState(false);
  const [selectedCenterId, setSelectedCenterId] = useState<
    number | undefined
  >();

  /** API */
  const { data: centers, isLoading } = useWarehouseList({
    search: searchText || undefined,
    page: currentPage - 1,
    size: pageSize,
  });

  const { handlePrint } = useTablePrint();

  /** Transform warehouses for table */
  const transformedCenters = useMemo((): Warehouse[] => {
    if (!centers?.data?.content) return [];
    return centers.data.content.map((item: Warehouse) => ({
      id: item.id,
      centerName: item.centerName,
      centerNumber: item.centerNo,
      capacity: item.capacity,
      location: item.location,
    }));
  }, [centers]);

  /** Handlers */
  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handleCreate = () => {
    setSelectedCenterId(undefined);
    setOpen(true);
  };

  const hideModal = () => {
    setOpen(false);
  };

  /** Columns */
  const centerColumns = [
    {
      key: "centerName",
      title: "Center Name",
      dataIndex: "centerName",
      align: "left" as const,
    },
    {
      key: "centerNumber",
      title: "Center No",
      dataIndex: "centerNumber",
      align: "left" as const,
    },
    {
      key: "capacity",
      title: "Capacity",
      dataIndex: "capacity",
      align: "left" as const,
    },
    {
      key: "location",
      title: "Location",
      dataIndex: "location",
      align: "left" as const,
    },
    {
      key: "actions",
      title: "Actions",
      align: "center" as const,
      render: (id: string, record: Warehouse) => {
        return (
          <Button
            variant="default"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedCenterId(record.id);
              setOpen(true);
            }}
          >
            <img
              src={Icons.pencilEdit}
              alt="edit"
              className="h-4 w-4 text-gray-100 mr-1"
            />
            Edit
          </Button>
        );
      },
    },
  ];

  return (
    <div
      className="bg-transparent lg:bg-white"
      style={{ borderRadius: 8.5, marginTop: 25 }}
    >
      <div className="flex flex-col sm:flex-row justify-between">
        <div className="relative w-full lg:w-64">
          <Input
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search centers by name"
            className="pl-10 rounded-2xl w-[308px] h-11"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            <img src={Icons.search} alt="search" className="w-6 h-6" />
          </div>
        </div>

        <div className="flex gap-2">
          <AppButton
            customIcon={Icons.printIcon}
            variant="outline"
            onClick={() =>
              handlePrint(centerColumns, transformedCenters, "Warehouse Report")
            }
            className="bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
          >
            Print
          </AppButton>

          <ExportButton
            columns={centerColumns}
            data={transformedCenters}
            fileName="centers"
            title="Warehouse Report"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: centers?.data?.totalElements ?? 0,
            }}
            customIcon={Icons.fileExport}
            className="bg-[#DDDDDD] hover:bg-gray-300"
          />

          <AppButton
            customIcon={Icons.addCircle}
            variant="success"
            onClick={handleCreate}
          >
            Create New
          </AppButton>
        </div>
      </div>

      {/** Desktop Table */}
      <div className="hidden md:block">
        <DataTable<Warehouse>
          data={transformedCenters}
          columns={centerColumns}
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: centers?.data?.totalElements ?? 0,
            onChange: handlePaginationChange,
          }}
          emptyIcon={<img src={Icons.noDataIcon} alt="No data" />}
          emptyText="No warehouses found"
          rowKey="id"
        />
      </div>

      <CreateWareHouseModal
        open={open}
        hideModal={hideModal}
        selectedId={selectedCenterId}
      />
    </div>
  );
};

export default WithAuth(WareHousePage);
